import React from 'react';
import { setUser as setSentryUser } from '@sentry/nextjs';
import {
  getLocalUser,
  getSessionToken,
  removeLocalUserAndSessionToken,
  setUserLocalStorageAndCookies,
} from '@mentimeter/user';
import {
  core,
  type UserFlagsT,
  type UserResponseT,
} from '@mentimeter/http-clients';
import {
  getLocalStorageVisitorToken,
  setLocalStorageVisitorToken,
} from '@mentimeter/visitor/visitor-token';
import { getStoredUtmFlags } from '@mentimeter/utm';
import { AppStateContext } from 'src/appState';

const _user = getLocalUser();
if (_user) setSentryUser({ id: String(_user.id) });

export function AppStateProvider({
  children,
  newlyGeneratedServerToken,
}: {
  children: React.ReactNode;
  newlyGeneratedServerToken: string;
}) {
  const [user, setUser] = React.useState<UserResponseT | null>(null);
  const [userFinishedLoading, setUserFinishedLoading] = React.useState(false);

  const localStorageVisitorToken = getLocalStorageVisitorToken();
  // we want to ensure that we don't re-render the page if we don't have a token in localStorage
  // so let's set it and use the one coming from the server as a fallback
  if (!localStorageVisitorToken) {
    setLocalStorageVisitorToken(newlyGeneratedServerToken);
  }

  const visitorToken = localStorageVisitorToken || newlyGeneratedServerToken;

  const logout = async () => {
    await removeLocalUserAndSessionToken();
    setUser(null);
    setSentryUser(null);
  };

  // triggers first, get user and set loading state
  React.useEffect(() => {
    const localUser = getLocalUser();
    const sessionToken = getSessionToken(localUser);

    if (sessionToken || localUser) {
      core()
        .users.fetchUser()
        .then(async ({ data }) => {
          await setUserLocalStorageAndCookies(data);
          setUser((state) => ({ ...state, ...data }));
        })
        .catch(async (e) => {
          if (e.response?.data?.status === 401) {
            // intercomLogout(); does not work - needs timeout (?).
            // user session has been revoked
            await logout();
          }
        });
    }

    setUser(localUser);
    setUserFinishedLoading(true);
  }, []);

  // triggers second
  React.useEffect(() => {
    if (!userFinishedLoading) return;

    // triggers third, save utm flags
    // this could be another effect, but we lack proper effect trigger since we get user from local storage and set it synchronously
    const localUser = getLocalUser();

    if (!localUser) return;

    const flags = getStoredUtmFlags(localUser.flags);

    if (!flags) return;

    core()
      .users.saveUtmFlags({
        flags: {
          utm_last:
            (flags.utm_last as UserFlagsT['utm_last']) ||
            localUser.flags?.utm_last,
          utm_first:
            (flags.utm_first as UserFlagsT['utm_first']) ||
            localUser.flags?.utm_first,
        },
      })
      .then(async ({ data }) => setUserLocalStorageAndCookies(data))
      .catch(async (error) => {
        if (error.response?.status === 401) {
          await logout();
        }
      });
  }, [userFinishedLoading]);

  return (
    <AppStateContext.Provider
      value={{ user, visitorToken, userFinishedLoading }}
    >
      {children}
    </AppStateContext.Provider>
  );
}
